import { HHSelect } from '@hinge-health/react-component-library';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useDynamicDrawerContext } from '../../../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../../../context/snackbar.context';
import {
  EligibilitySearchDepthEnum,
  GetBillsDocument,
  useBulkEligibilityCheckMutation,
} from '../../../../types';
import {
  persistStatus,
  StatusStorageKeys,
} from '../../../../utils/local-storage-utils';
import {
  ActionTypeDisplayText,
  GridLevelName,
} from '../../../grid-components/grid-types';
import { PanelAlert } from '../../../grid-components/panel-alert';
import { PanelLayout } from '../../../grid-components/panel-layout';
import { PanelTitle } from '../../../grid-components/panel-title';
import { noteInvalid } from '../../../grid-components/panel-validation';
import { BillTypeDataList } from '../bill-management-types';
import { BillPanelProps } from '../panel-types';
import { SelectedBillsGrid } from './selected-grid';

export const CheckEligibilityPanel = ({
  selectedRows,
  clients,
  insurers,
  onClearSelections,
}: BillPanelProps): JSX.Element => {
  const { setIsOpen, setTitle } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();
  const [panelSelectedRows, setPanelSelectedRows] =
    useState<BillTypeDataList>(selectedRows);
  const [note, setNote] = useState<string>('');
  const [eligibilityCheckType, setEligibilityCheckType] =
    useState<EligibilitySearchDepthEnum>(EligibilitySearchDepthEnum.Deep);
  const [eligibilityCheck, { loading }] = useBulkEligibilityCheckMutation();

  const handleCheckEligibility = async (): Promise<void> => {
    try {
      const result = await eligibilityCheck({
        variables: {
          bulkEligibilityCheckInput: {
            billIds: panelSelectedRows.map(row => row.billId),
            mode: eligibilityCheckType,
            note,
          },
        },
        refetchQueries: [GetBillsDocument],
      });
      if (result.data?.bulkEligibilityCheck) {
        persistStatus(
          {
            ...result.data.bulkEligibilityCheck,
            initiatedAt: dayjs().toISOString(),
          },
          StatusStorageKeys.eligibilityCheckStatus,
          result.data.bulkEligibilityCheck.workflowId,
        );
      }
      setSnackbarState(
        `Eligibility check initiated with workflow ID: ${result.data?.bulkEligibilityCheck?.workflowId ?? ''}`,
        'success',
        5000,
      );
      onClearSelections();
      setTimeout(() => setIsOpen(false), 500);
    } catch (e) {
      setSnackbarState(
        `Failed to initiate eligibility check with error: ${e instanceof Error ? e.message : 'Unknown error'}`,
        'error',
        5000,
      );
    }
  };

  const handleSelectionChanged = (selectedGridRows: BillTypeDataList): void => {
    setPanelSelectedRows(selectedGridRows);
    setTitle(
      <PanelTitle
        actionType={ActionTypeDisplayText.CHECK_ELIGIBILITY}
        rowCount={selectedGridRows.length}
        gridLevel={GridLevelName.BILL}
      />,
    );
  };

  return (
    <PanelLayout
      selectedGrid={
        <SelectedBillsGrid
          selectedRows={panelSelectedRows}
          setPanelSelectedRows={handleSelectionChanged}
          clients={clients}
          insurers={insurers}
        />
      }
      additionalInputs={
        <HHSelect
          label="Eligibility Check Type *"
          selectOptions={[
            {
              value: EligibilitySearchDepthEnum.Standard,
              displayName: 'Standard',
            },
            { value: EligibilitySearchDepthEnum.Deep, displayName: 'Deep' },
          ]}
          initialSelectValue={eligibilityCheckType}
          onChange={(e): void =>
            setEligibilityCheckType(
              e.target.value as EligibilitySearchDepthEnum,
            )
          }
        />
      }
      alert={<PanelAlert drawerMode="check-eligibility" />}
      note={note}
      setNote={setNote}
      setIsOpen={setIsOpen}
      isSubmitting={loading}
      isInvalid={noteInvalid(note)}
      actionLabel="Check Eligibility"
      handleSubmit={handleCheckEligibility}
    />
  );
};
